import { css } from '@emotion/react';
import { MenuItem, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { isValidDate } from '../../../../util/date-utilts';
import { FMT } from '../../../../util/formatter-service';
import { EquityHoldingsView, equityHoldingsAsOfDateJ, equityHoldingsFundIdJ, equityHoldingsViewJ } from './state';

/** Views on the left, filters pushed to the right, as the design lays them out. */
const toolbar = css`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0 0.5rem 0.75rem;
`;

const spacer = css`
  flex: 1;
  min-width: 0;
`;

const filterGroup = css`
  align-items: center;
  display: flex;
  gap: 0.5rem;
`;

const fundSelect = css`
  min-width: 11rem;
`;

const datePicker = css`
  width: 11rem;
`;

export const AllFundsValue = 'all';

interface IEquityHoldingsToolbarProps {
  companyId: number;
  /** Every fund present in the unfiltered data. */
  fundIds: number[];
}

export function EquityHoldingsToolbar({ companyId, fundIds }: IEquityHoldingsToolbarProps) {
  const [view, setView] = useAtom(equityHoldingsViewJ(companyId));
  const [fundId, setFundId] = useAtom(equityHoldingsFundIdJ(companyId));
  const [asOfDate, setAsOfDate] = useAtom(equityHoldingsAsOfDateJ(companyId));

  const onViewChange = useCallback(
    (_: unknown, next: EquityHoldingsView | null) => {
      // ToggleButtonGroup reports null when the active button is clicked again; keep the view.
      if (next !== null) {
        setView(next);
      }
    },
    [setView]
  );

  // MUI's field picker emits an Invalid Date as soon as every section is filled, even for something
  // impossible like 02/31 — storing it would serialise to `date=NaN-NaN-NaN` and break the fetch.
  // A half-typed or cleared date reverts to today rather than freezing the card.
  const onDateChange = useCallback(
    (next: Date | null) => {
      if (next === null) {
        setAsOfDate(null);
        return;
      }
      if (isValidDate(next)) {
        setAsOfDate(next);
      }
    },
    [setAsOfDate]
  );

  const onFundChange = useCallback(
    (value: string) => {
      setFundId(value === AllFundsValue ? null : Number(value));
    },
    [setFundId]
  );

  return (
    <div css={toolbar}>
      <ToggleButtonGroup
        aria-label='equity holdings view'
        color='primary'
        exclusive
        size='small'
        value={view}
        onChange={onViewChange}
      >
        <ToggleButton value='overview'>Security Overview</ToggleButton>
        <ToggleButton value='history'>Security History</ToggleButton>
      </ToggleButtonGroup>

      <div css={spacer} />

      <div css={filterGroup}>
        <TextField
          aria-label='fund'
          css={fundSelect}
          select
          size='small'
          value={fundId === null ? AllFundsValue : String(fundId)}
          onChange={(event) => onFundChange(event.target.value)}
        >
          <MenuItem value={AllFundsValue}>All Funds</MenuItem>
          {fundIds.map((id) => (
            <MenuItem key={id} value={String(id)}>
              {FMT.format('fund', id)}
            </MenuItem>
          ))}
        </TextField>

        <Typography variant='body2' color='text.secondary' component='label'>
          As of:
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            css={datePicker}
            // `null` is today; the picker shows the effective date either way.
            value={asOfDate ?? new Date()}
            onChange={onDateChange}
            slotProps={{ textField: { size: 'small', 'aria-label': 'as of date' } }}
          />
        </LocalizationProvider>
      </div>
    </div>
  );
}
